import { CodeHighlight } from '@mantine/code-highlight';
import { Alert, Anchor, Code, List, Paper, Space, Text, Title } from '@mantine/core';
import { ExternalLink } from '../../components/ExternalLink';
import { PageWrapper } from '../../components/PageWrapper/PageWrapper';
import { useVersion } from '../../versions/url-version';
import { apiSlug } from '../../versions/versions';

export function Authentication() {
  const version = useVersion();
  const apiVersion = apiSlug(version);

  return (
    <PageWrapper>
      <Title order={1} mb="md">
        Authentication and Certificates
      </Title>
      <Alert variant="light" color="blue" mb="lg">
        ✅ Get authenticated and start making API requests with the Emissions Calculators.
      </Alert>

      <Title order={2} mb="md">
        mTLS
      </Title>
      <Text mb="lg">
        To securely authenticate, the API uses mutual TLS (mTLS) to ensure that both the client and server are who they
        say they are. You'll need a certificate and private key to make requests to the API.
      </Text>

      <Title order={2} mb="md">
        Getting a Certificate
      </Title>
      <Text mb="md">
        To get a certificate, you'll need to generate a Certificate Signing Request (CSR) and submit it to us via{' '}
        <ExternalLink href="https://www.aiaeap.com/partner">the API Integrator Application form</ExternalLink>.
      </Text>

      <Paper withBorder p="md" mb="md">
        <Text size="sm" fw={500} mb="xs">
          Generate Your Private Key
        </Text>
        <Code block>{`openssl genrsa -out carbon-calculator-integration.key 4096`}</Code>
      </Paper>

      <Paper withBorder p="md" mb="md">
        <Text size="sm" fw={500} mb="xs">
          Create a Certificate Signing Request from the Key
        </Text>
        <Code block>
          {`openssl req -new -key carbon-calculator-integration.key -out certificate-request.csr`}
        </Code>

      </Paper>

      <Text mb="md">
        When prompted, provide the two-letter code for your country, your state or province, and your organisation 
        name. If you are requesting a certificate as an individual, use your own name.
      </Text>
      <Space h="xs" />
      <Text  mb="md">
        For example:
      </Text>

      <Code block>
        {`~ % openssl req -new -key carbon-calculator-integration.key -out certificate-request.csr 
You are about to be asked to enter information that will be incorporated
into your certificate request.
What you are about to enter is what is called a Distinguished Name or a DN.
There are quite a few fields but you can leave some blank
For some fields there will be a default value,
If you enter '.', the field will be left blank.
-----
Country Name (2 letter code) [AU]:AU
State or Province Name (full name) [Some-State]:New South Wales
Locality Name (eg, city) []:
Organization Name (eg, company) [Internet Widgits Pty Ltd]:REPLACE ORG NAME HERE
Organizational Unit Name (eg, section) []:
Common Name (e.g. server FQDN or YOUR name) []:
Email Address []:

Please enter the following 'extra' attributes
to be sent with your certificate request
A challenge password []:
An optional company name []:
~ % `}
      </Code>
<Space h="md" />
      <Alert variant="light" color="blue" mb="lg">
        🚨 Once you receive your certificate, it's important that you keep it secure and do not share it with anyone. If
        you need to revoke the certificate, you can do so by contacting us at{' '}
        <Anchor href="mailto:contact@aginnovationaustralia.com.au">contact@aginnovationaustralia.com.au</Anchor>.
      </Alert>

      <Title order={2} mb="md">
        Making Requests
      </Title>
      <Text mb="md">
        Now that you have a private key and certificate, you can make requests to the API. Here's an example using cURL:
      </Text>

      <Paper withBorder p="md" mb="md">
        <Text size="sm" fw={500} mb="xs">
          Example cURL Request
        </Text>
        <CodeHighlight
          language="bash"
          code={`
          curl --request POST \\
--key carbon-calculator-integration.key \\
--cert carbon-calculator-integration.pem \\
-v \\
--url ${__API_URL__}/calculator/${apiVersion}/beef \\
--header 'Accept: application/json' \\
--header 'Content-Type: application/json' \\
--data '{}'`}
        />
      </Paper>

      <Text mb="lg">
        You'll need to implement the same certificate and key usage in your application to authenticate with the API. We
        have some example implementations, so if you're having trouble just let us know what you're using and we'll help
        you out.
      </Text>

      <Alert variant="light" color="blue" mb="lg">
        🔎 If you're building a web based application that needs to perform calculations using the Emissions Calculators
        API, it is recommended that you import the NPM package directly rather than making REST API requests. This helps
        to avoid the problem of making mTLS requests from the browser.
      </Alert>

      <Title order={2} mb="md">
        User Agent Header
      </Title>
      <Text mb="md">
        In order for the server to accept your requests, you'll need to specify a user agent. We recommend you set this
        to the name of your organisation, for example:
      </Text>

      <Code block mb="md">
        {`User-Agent: my-organisation-name-integration`}
      </Code>

      <Text mb="md">If you do not specify a user agent, you will get a 403 response with a body like:</Text>

      <Code block mb="md">
        {`{"message":"Forbidden"}`}
      </Code>

      <Text mb="lg">
        This is not the only reason that you might get this message, but it is the most common scenario where
        integration partners experience this issue.
      </Text>

      <Title order={2} mb="md">
        Validating Responses
      </Title>
      <Text mb="md">
        Responses will come back signed with our certificate. You can validate the response by checking:
      </Text>

      <List mb="md">
        <List.Item>The certificate is not expired.</List.Item>
        <List.Item>The certificate is trusted by your operating system's trust store.</List.Item>
        <List.Item>
          The certificate's common name is <Code>*.aiaapi.com</Code>.
        </List.Item>
      </List>

      <Text mb="md">For example, here's code that will do this for various languages (more coming soon!):</Text>

      <Paper withBorder p="md" mb="md">
        <Text size="sm" fw={500} mb="xs">
          .NET (C#)
        </Text>
        <CodeHighlight
          language="csharp"
          code={`
          Func<HttpRequestMessage, X509Certificate2?, X509Chain?, SslPolicyErrors, bool> ValidateServerCertificate = (sender, certificate, chain, sslPolicyErrors) => {
    if (certificate == null) return false;
    if (certificate.NotBefore > DateTime.Now || certificate.NotAfter < DateTime.Now)
    {
        Console.WriteLine("Server certificate is expired or not yet valid");
        return false;
    }

    if (certificate.Subject != "CN=*.aiaapi.com") 
    {
        Console.WriteLine("Server certificate is not issued for *.aiaapi.com");
        return false;
    }

    if (!certificate.Verify()) {
        Console.WriteLine("Server certificate did not pass verify check.");
        return false;
    }

    return true;
};`}
        />
      </Paper>

      <Title order={2} mb="md">
        Need Help?
      </Title>
      <Text>
        Email us at{' '}
        <Anchor href="mailto:contact@aginnovationaustralia.com.au">contact@aginnovationaustralia.com.au</Anchor> and
        we'll do our best to reply within 48 hours.
      </Text>
    </PageWrapper>
  );
}
